(() => {
  const init = (root) => {
    if (!root || !root.matches('[data-tan-hotel-brief]') || root.dataset.hotelBriefReady) return;
    root.dataset.hotelBriefReady = 'true';

    const i18nNode = root.querySelector('[data-hotel-brief-i18n]');
    let i18n = {};
    try {
      i18n = JSON.parse(i18nNode?.textContent || '{}');
    } catch (_) {
      i18n = {};
    }

    const text = (key) => String(i18n[key] || '');
    const interpolate = (key, values) => Object.entries(values).reduce(
      (result, [name, value]) => result.split(`__${name.toUpperCase()}__`).join(String(value)),
      text(key),
    );

    const buttons = Array.from(root.querySelectorAll('[data-hotel-choice]'));
    const dateInput = root.querySelector('[data-hotel-date]');
    const distanceInput = root.querySelector('[data-hotel-distance]');
    const widthInput = root.querySelector('[data-hotel-width]');
    const contextHeading = root.querySelector('[data-hotel-context-heading]');
    const contextCopy = root.querySelector('[data-hotel-context-copy]');
    const contextMeta = root.querySelector('[data-hotel-context-meta]');
    const resultHeading = root.querySelector('[data-hotel-result-heading]');
    const resultCopy = root.querySelector('[data-hotel-result-copy]');
    const copyArea = root.querySelector('[data-hotel-copy-text]');
    const copyButton = root.querySelector('[data-hotel-copy]');
    const secondaryLink = root.querySelector('[data-hotel-secondary]');

    const selected = {
      property: root.querySelector('[data-hotel-choice][data-choice-group="property"].is-active'),
      role: root.querySelector('[data-hotel-choice][data-choice-group="role"].is-active'),
      context: root.querySelector('[data-hotel-choice][data-choice-group="context"].is-active'),
    };

    const payload = (id) => root.querySelector(`[data-hotel-payload="${CSS.escape(id)}"]`);
    const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const listText = (id) => {
      const template = payload(id);
      if (!template) return [];
      return Array.from(template.content.querySelectorAll('li')).map((item) => clean(item.textContent)).filter(Boolean);
    };

    const formatDate = () => {
      if (!dateInput?.value) return text('not_added');
      const parsed = new Date(`${dateInput.value}T12:00:00`);
      if (Number.isNaN(parsed.getTime())) return dateInput.value;
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        year: 'numeric', month: 'short', day: 'numeric',
      }).format(parsed);
    };

    const selectValue = (input, fallback) => clean(input?.selectedOptions?.[0]?.textContent) || fallback;
    const widthValue = () => widthInput?.value
      ? interpolate('width_with_unit', { width: clean(widthInput.value) })
      : text('not_added');

    const updateContext = () => {
      const date = formatDate();
      const distance = selectValue(distanceInput, text('not_selected'));
      const width = widthValue();
      const hasSpatialData = Boolean(distanceInput?.value || widthInput?.value);

      if (contextHeading) {
        contextHeading.textContent = hasSpatialData
          ? root.dataset.contextReadyHeading
          : root.dataset.contextEmptyHeading;
      }
      if (contextCopy) {
        contextCopy.textContent = hasSpatialData
          ? root.dataset.contextReadyText
          : root.dataset.contextEmptyText;
      }
      if (contextMeta) {
        contextMeta.textContent = interpolate('context_meta', { date, distance, width });
      }
    };

    const update = () => {
      const property = selected.property;
      const role = selected.role;
      const context = selected.context;
      if (!property || !role || !context) return;

      const propertyTitle = clean(property.dataset.choiceTitle);
      const roleTitle = clean(role.dataset.choiceTitle);
      const contextTitle = clean(context.dataset.choiceTitle);
      const combinedCopy = [property.dataset.choiceNote, role.dataset.choiceNote, context.dataset.choiceNote]
        .map(clean).filter(Boolean).join(' ');

      if (resultHeading) resultHeading.textContent = `${propertyTitle} · ${roleTitle} · ${contextTitle}`;
      if (resultCopy) resultCopy.textContent = combinedCopy;

      ['property', 'role', 'context'].forEach((group) => {
        const target = root.querySelector(`[data-hotel-list="${group}"]`);
        const source = payload(selected[group].dataset.choiceId);
        const content = source?.content.querySelector('[data-hotel-checklist]');
        if (target && content) target.innerHTML = content.innerHTML;
      });

      if (secondaryLink) {
        const label = clean(context.dataset.guideLabel);
        const link = clean(context.dataset.guideLink);
        secondaryLink.hidden = !(label && link);
        if (label && link) {
          secondaryLink.textContent = label;
          secondaryLink.href = link;
        }
      }

      if (copyArea) {
        const date = formatDate();
        const distance = selectValue(distanceInput, text('not_selected'));
        const width = widthValue();
        const lines = [
          text('brief_title'),
          interpolate('target_date', { date }),
          interpolate('property_type', { property: propertyTitle }),
          interpolate('sign_role', { role: roleTitle }),
          interpolate('installation_context', { context: contextTitle }),
          interpolate('viewing_distance', { distance }),
          interpolate('wall_width', { width }),
          '',
          interpolate('planning_direction', { direction: combinedCopy }),
          '',
          interpolate('property_details', { details: listText(property.dataset.choiceId).join('; ') }),
          interpolate('guest_details', { details: listText(role.dataset.choiceId).join('; ') }),
          interpolate('installation_details', { details: listText(context.dataset.choiceId).join('; ') }),
          '',
          text('attachments'),
        ];
        copyArea.value = lines.join('\n');
      }

      updateContext();
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.dataset.choiceGroup;
        if (!group || !selected[group]) return;
        buttons.filter((item) => item.dataset.choiceGroup === group).forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        selected[group] = button;
        update();
      });
    });

    [dateInput, distanceInput, widthInput].forEach((input) => input?.addEventListener('input', update));

    copyButton?.addEventListener('click', async () => {
      if (!copyArea) return;
      let copied = false;
      try {
        await navigator.clipboard.writeText(copyArea.value);
        copied = true;
      } catch (_) {
        copyArea.focus();
        copyArea.select();
        try {
          copied = document.execCommand('copy');
        } catch (_) {
          copied = false;
        }
      }
      const original = copyButton.dataset.copyLabel || copyButton.textContent;
      const status = copied ? (copyButton.dataset.copiedLabel || text('copied')) : text('copy_error');
      copyButton.textContent = status;
      copyButton.setAttribute('aria-label', status);
      window.setTimeout(() => {
        copyButton.textContent = original;
        copyButton.setAttribute('aria-label', original);
      }, 1800);
    });

    update();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-tan-hotel-brief]')) init(scope);
    scope.querySelectorAll?.('[data-tan-hotel-brief]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
